import { getAuthHeaders } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3010/api';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  dni?: string;
  address?: string;
  city?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other';
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  preferences: {
    notifications: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
    language: string;
    timezone: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileData {
  fullName?: string;
  email?: string;
  phone?: string;
  dni?: string;
  address?: string;
  city?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other';
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  preferences?: {
    notifications: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
    language: string;
    timezone: string;
  };
}

/**
 * Obtener el perfil del usuario actual
 */
export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    const response = await fetch(`${API_BASE_URL}/client/profile`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al obtener el perfil');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Actualizar el perfil del usuario
 */
export const updateUserProfile = async (profileData: UpdateProfileData): Promise<UserProfile> => {
  try {
    const response = await fetch(`${API_BASE_URL}/client/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error al actualizar el perfil');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Cambiar la contraseña del usuario
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    const response = await fetch(`${API_BASE_URL}/client/profile/password`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = 'Error al cambiar la contraseña';

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        errorMessage = response.statusText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    // La respuesta es exitosa - verificar si hay contenido para consumir
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        await response.json(); // Consumir la respuesta JSON
      }
    } catch (parseError) {
      // No hay problema si no hay contenido en respuesta exitosa
    }

    // La operación fue exitosa
    return;
  } catch (error) {
    console.error('Error changing password:', error);

    // Manejar diferentes tipos de errores
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('La solicitud ha tardado demasiado. Verifica tu conexión e intenta de nuevo.');
      }

      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
      }

      if (error.message.includes('CORS')) {
        throw new Error('Error de configuración del servidor. Contacta al administrador.');
      }
    }

    // Si es un error de red/conexión genérico
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
    }

    throw error;
  }
};

/**
 * Eliminar la cuenta del usuario
 */
export const deleteAccount = async (password: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/client/profile`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error al eliminar la cuenta');
    }
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
};
